import _asyncToGenerator from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _regeneratorRuntime from "/home/runner/work/web/web/node_modules/next/dist/compiled/@babel/runtime/regenerator/index.js";
import { useEffect, createContext, useState } from 'react';
import useSWR from 'swr';
import { HTTPMethod } from 'http-method-enum';
import { useRouter } from 'next/router';
import { API_AUTH_STATUS, API_USER, API_USER_ENTITLEMENT } from '@store/constants/routes';
import { captureException } from '@yoweb/utils/sentry';
import { createApiError, fetchJson } from '@yoweb/utils/fetchJson';
import { fetcher } from '@yoweb/utils/common';
import { toErrorObject } from '@yoweb/utils/error';
import { defaultHeaders as headers } from '@yoweb/utils/defaultHeaders';
import { negotiateTimezone } from '@yoweb/utils/i18n';
import { assertIsString } from '@yoweb/utils/isString';
import { loginEvent } from '@yoweb/utils/analytics/mixpanel';
import { jsx as _jsx } from "react/jsx-runtime";
export var AuthenticationType;

(function (AuthenticationType) {
  AuthenticationType["auth0"] = "auth0";
  AuthenticationType["unauthenticated"] = "unauthenticated";
})(AuthenticationType || (AuthenticationType = {}));

export var UserContext = /*#__PURE__*/createContext(undefined);

var useCombinedUserState = function useCombinedUserState() {
  var _useRouter = useRouter(),
      locale = _useRouter.locale;

  assertIsString(locale);

  var _useState = useState(undefined),
      user = _useState[0],
      setUser = _useState[1];

  var _useState2 = useState(true),
      isLoading = _useState2[0],
      setIsLoading = _useState2[1];

  var _useState3 = useState(undefined),
      error = _useState3[0],
      setError = _useState3[1];

  var _useSWR = useSWR(API_AUTH_STATUS, fetcher, {
    shouldRetryOnError: false
  }),
      authStatusData = _useSWR.data;

  var hasFullUser = !!user;

  var _useSWR2 = useSWR(hasFullUser ? API_USER_ENTITLEMENT : null, fetcher, {
    errorRetryInterval: 30000,
    errorRetryCount: 5
  }),
      entitlement = _useSWR2.data; // Fetch the user when auth status changes


  useEffect(function () {
    if (!authStatusData) {
      return;
    }

    var getUser = /*#__PURE__*/function () {
      var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
        var _user;

        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) {
            switch (_context.prev = _context.next) {
              case 0:
                setIsLoading(true);
                _context.prev = 1;
                _context.next = 4;
                return fetchJson(API_USER, {
                  method: HTTPMethod.GET,
                  headers: {
                    'Cache-Control': 'no-cache'
                  }
                });

              case 4:
                _user = _context.sent;

                if (_user) {
                  setUser(_user);
                  loginEvent(_user.id);
                } // eslint-disable-next-line no-catch-all/no-catch-all


                _context.next = 11;
                break;

              case 8:
                _context.prev = 8;
                _context.t0 = _context["catch"](1);
                setError(toErrorObject(_context.t0));

              case 11:
                _context.prev = 11;
                setIsLoading(false);
                return _context.finish(11);

              case 14:
              case "end":
                return _context.stop();
            }
          }
        }, _callee, null, [[1, 8, 11, 14]]);
      }));

      return function getUser() {
        return _ref.apply(this, arguments);
      };
    }();

    if (authStatusData !== null && authStatusData !== void 0 && authStatusData.isAuthenticated) {
      void getUser();
    } else {
      setIsLoading(false);
      setUser(undefined);
    }
  }, [authStatusData]);

  var update = /*#__PURE__*/function () {
    var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(address, phoneNumber) {
      var payload, res, updatedUser;
      return _regeneratorRuntime.wrap(function _callee2$(_context2) {
        while (1) {
          switch (_context2.prev = _context2.next) {
            case 0:
              if (user) {
                _context2.next = 2;
                break;
              }

              return _context2.abrupt("return");

            case 2:
              payload = {
                locale: locale,
                address: address,
                telephoneNumber: {
                  telephoneNumber: phoneNumber
                },
                inviteCode: null
              }; // don't use fetchJson here as we want to conditionally send
              // exceptions to sentry based on the type of the returned error

              _context2.next = 5;
              return fetch(API_USER, {
                method: HTTPMethod.PATCH,
                headers: headers,
                body: JSON.stringify(payload)
              });

            case 5:
              res = _context2.sent;
              _context2.next = 8;
              return res.json();

            case 8:
              updatedUser = _context2.sent;

              if (res.ok) {
                _context2.next = 11;
                break;
              }

              throw createApiError(updatedUser, res);

            case 11:
              if (updatedUser) {
                setUser(updatedUser);
                loginEvent(updatedUser.id);
              }

            case 12:
            case "end":
              return _context2.stop();
          }
        }
      }, _callee2);
    }));

    return function update(_x, _x2) {
      return _ref2.apply(this, arguments);
    };
  }();

  useEffect(function () {
    if (error) {
      captureException(error);
    }
  }, [error]);
  var timeZone = negotiateTimezone(locale, user);

  if (user === undefined && isLoading) {
    return {
      type: AuthenticationType.unauthenticated,
      isAuthenticated: false,
      isLoading: true,
      profile: undefined,
      entitlement: undefined,
      timeZone: timeZone
    };
  }

  if (user === undefined) {
    return {
      type: AuthenticationType.unauthenticated,
      isAuthenticated: false,
      isLoading: false,
      profile: undefined,
      entitlement: undefined,
      timeZone: timeZone
    };
  }

  if (user && !error && !user.error) {
    return {
      update: update,
      type: AuthenticationType.auth0,
      isAuthenticated: true,
      isLoading: false,
      profile: user,
      error: undefined,
      entitlement: entitlement,
      timeZone: timeZone
    };
  }

  return {
    type: AuthenticationType.unauthenticated,
    isAuthenticated: false,
    isLoading: false,
    profile: undefined,
    error: error,
    entitlement: undefined,
    timeZone: timeZone
  };
};

export function UserContextProvider(_ref3) {
  var children = _ref3.children;
  var state = useCombinedUserState();
  return /*#__PURE__*/_jsx(UserContext.Provider, {
    value: state,
    children: children
  });
}